@let activeJobForValuation = activeJobForValuation$ | async;
@let readyJobForValuation = readyJobForValuation$ | async;

<div class="dialog-header">
    <h2 mat-dialog-title>Run Algorithmic DMV</h2>
    <button mat-icon-button mat-dialog-close class="dialog-close" aria-label="Close" data-test="dialog-close">
        <mat-icon>close</mat-icon>
    </button>
</div>

<mat-dialog-content class="dmv-dialog-content">

    @if (!hasValuation) {
        <div class="empty-state">
            <mat-icon class="empty-state-icon">stacked_line_chart</mat-icon>
            <h3>No Bottom-Up fund valuation for this report</h3>
            <p>
                The algorithmic DMV batch runs inside the context of a Bottom-Up fund
                valuation. Create one for the current report, then come back to run the batch.
            </p>
            <div class="empty-state-actions">
                <button mat-flat-button color="primary" type="button" (click)="requestCreateValuation()">
                    Create fund valuation
                </button>
            </div>
        </div>
    } @else {
        <form [formGroup]="form">

            @if (activeJobForValuation) {
                <div class="hint hint--warning">
                    <mat-icon class="hint-icon">hourglass_top</mat-icon>
                    <span>A run is already in progress for this fund valuation. Wait for it to
                        finish before starting another.</span>
                </div>
            } @else if (readyJobForValuation) {
                <div class="hint hint--warning">
                    <mat-icon class="hint-icon">warning</mat-icon>
                    <span>{{ readyJobForValuation.pendingReviewCount }} result(s) from a previous run
                        are awaiting review for this fund valuation. Starting a new run will supersede
                        them — they'll be marked skipped.</span>
                </div>
            }

            <section class="dialog-section">
                <h3>Assets in scope</h3>
                <div class="select-row">
                    <button mat-stroked-button type="button" (click)="selectEligible()">Eligible only</button>
                    <button mat-stroked-button type="button" (click)="selectAll()">Select all</button>
                    <button mat-stroked-button type="button" (click)="clearAll()">Clear</button>
                </div>
                <div class="select-row select-row--agent">
                    <span class="bulk-label">Set agent review for selected:</span>
                    <button mat-stroked-button type="button" (click)="setAllSelectedAgentMode(AGENT_MODE.Off)">Off</button>
                    <button mat-stroked-button type="button" (click)="setAllSelectedAgentMode(AGENT_MODE.Quick)">Quick</button>
                    <button mat-stroked-button type="button" (click)="setAllSelectedAgentMode(AGENT_MODE.Deep)">Deep</button>
                </div>
                <div class="asset-list">
                    @for (asset of data.assets; track asset.id) {
                        @let inScope = isAssetSelected(asset.id);
                        <div class="asset-row" [class.asset-row--ineligible]="!asset.isEligible">
                            <mat-checkbox class="asset-row-check"
                                [checked]="inScope"
                                [disabled]="!asset.isEligible"
                                (change)="toggleAsset(asset.id, $event.checked)"
                                [attr.aria-label]="asset.name">
                            </mat-checkbox>
                            <div class="asset-row-main">
                                <span class="asset-row-name" [title]="asset.name">{{ asset.name }}</span>
                                <span class="asset-row-metrics">
                                    <span class="asset-metric">NAV {{ asset.nav !== undefined ? (asset.nav | decimalFormat:'currency') : '—' }}</span>
                                    <span class="asset-metric">Unreal. TVPI {{ asset.unrealizedTvpi !== undefined ? (asset.unrealizedTvpi | decimalFormat:'multiple') : '—' }}</span>
                                    <span class="asset-metric">Total TVPI {{ asset.totalTvpi !== undefined ? (asset.totalTvpi | decimalFormat:'multiple') : '—' }}</span>
                                </span>
                            </div>

                            <span class="dmv-source-chip"
                                  [class.dmv-source-chip--none]="asset.existingDmvSource === 'NONE'"
                                  [class.dmv-source-chip--algorithm]="asset.existingDmvSource === 'ALGORITHM'"
                                  [class.dmv-source-chip--user]="asset.existingDmvSource === 'USER'">
                                @switch (asset.existingDmvSource) {
                                    @case ('NONE') { No DMV }
                                    @case ('ALGORITHM') { Algorithm DMV }
                                    @case ('USER') { User DMV }
                                }
                            </span>

                            @if (asset.isEligible) {
                                <span class="eligibility-chip eligibility-chip--ok">
                                    <mat-icon class="eligibility-chip-icon">check_circle</mat-icon>
                                    Eligible
                                </span>
                            } @else {
                                <span class="eligibility-chip eligibility-chip--blocked"
                                      [title]="asset.ineligibilityReason ?? ''">
                                    <mat-icon class="eligibility-chip-icon">block</mat-icon>
                                    {{ asset.ineligibilityReason ?? "Ineligible" }}
                                </span>
                            }

                            <mat-button-toggle-group class="agent-toggle"
                                [hideSingleSelectionIndicator]="true"
                                [value]="modeFor(asset.id)"
                                [disabled]="!asset.isEligible || !inScope"
                                (change)="setAgentMode(asset.id, $event.value)"
                                aria-label="Agent review depth">
                                <mat-button-toggle [value]="AGENT_MODE.Off">Off</mat-button-toggle>
                                <mat-button-toggle [value]="AGENT_MODE.Quick">Quick</mat-button-toggle>
                                <mat-button-toggle [value]="AGENT_MODE.Deep">Deep</mat-button-toggle>
                            </mat-button-toggle-group>
                        </div>
                    }
                </div>
                <div class="hint">
                    Ineligible assets can't be processed by the algorithm — typically because
                    no DMV exists yet (bootstrap via the single-asset DMV button first) or
                    the position has been realised.
                </div>
                <div class="hint">
                    Per asset, choose how deep the agent reviews it. <strong>Off</strong> runs the
                    algorithm only; <strong>Quick</strong> adds a fast triage read (no web search);
                    <strong>Deep</strong> runs the full methodology with tools and market research.
                    The agent only annotates — it never changes the algorithm's numbers.
                    @if (reviewedCount > 0) {
                        <span class="review-count">{{ reviewedCount }} of {{ form.controls.assetIds.value?.length ?? 0 }} selected will get an agent review.</span>
                    }
                </div>
            </section>

            <section class="dialog-section">
                <h3>Algorithm inputs</h3>
                <div class="algorithm-inputs">
                    <mat-form-field appearance="outline" class="algorithm-input">
                        <mat-label>GP quality</mat-label>
                        <mat-select formControlName="gpQuality">
                            @for (q of gpQualities; track q) {
                                <mat-option [value]="q">{{ q }}</mat-option>
                            }
                        </mat-select>
                        @if (form.controls.gpQuality.hasError("required")) {
                            <mat-error>GP quality is required to run the batch.</mat-error>
                        }
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="algorithm-input">
                        <mat-label>Expected gross IRR</mat-label>
                        <input matInput type="number" formControlName="gpIrrPercent"
                               min="0.01" step="1">
                        <span matTextSuffix>%</span>
                        @if (form.controls.gpIrrPercent.hasError("required")
                            || form.controls.gpIrrPercent.hasError("min")) {
                            <mat-error>Enter an expected gross IRR greater than 0.</mat-error>
                        }
                    </mat-form-field>
                </div>
                @if (!hasGp) {
                    <div class="hint hint--warning">
                        <mat-icon class="hint-icon">warning</mat-icon>
                        <span>No general partner is set for this fund, so this quality can't be
                            saved anywhere — it's used for this run only.</span>
                    </div>
                } @else if (gpQualityWasUnset) {
                    <div class="hint hint--warning">
                        <mat-icon class="hint-icon">warning</mat-icon>
                        <span>This GP had no quality set. Pick one to continue — it's saved
                            back onto the general partner when the batch runs.</span>
                    </div>
                } @else {
                    <div class="hint">
                        Pre-filled from the general partner. Changing it here updates the GP
                        and is used for every asset in this run.
                    </div>
                }
                <div class="hint">
                    The expected gross IRR is applied to every selected asset's DMV for this run.
                </div>
            </section>

            <section class="dialog-section">
                <h3>Overwrite policy</h3>
                <mat-radio-group formControlName="overwriteMode" class="overwrite-modes">
                    @for (mode of overwriteModes; track mode.value) {
                        <mat-radio-button [value]="mode.value" class="overwrite-mode-card">
                            <div class="overwrite-mode-label">{{ mode.label }}</div>
                            <div class="overwrite-mode-description">{{ mode.description }}</div>
                        </mat-radio-button>
                    }
                </mat-radio-group>
            </section>

            <div class="disclaimer">
                <mat-icon class="disclaimer-icon">info</mat-icon>
                <span>Generated DMV values are staged for review on the next screen. They are
                    not written to your asset valuations until you accept each one.</span>
            </div>
        </form>
    }
</mat-dialog-content>

@if (hasValuation) {
    <mat-dialog-actions align="end">
        <button mat-flat-button color="primary" type="button" (click)="submit()"
                [disabled]="form.invalid || !!activeJobForValuation">
            Run batch
        </button>
    </mat-dialog-actions>
}
